import { Container, Graphics } from 'pixi.js';
import type { Pane } from 'tweakpane';
import { registerResizableObject } from '../../../utils/pixi/registerResizableObject';

export interface LayoutGalleryDisplaySettings {
  backgroundColor: string | number;
  backgroundAlpha: number;
  layoutParentPosition: { x: number, y: number };
}

export class LayoutGalleryDisplay extends Container {
  private readonly displaySettings: LayoutGalleryDisplaySettings = {
    backgroundColor: 'white',
    backgroundAlpha: 1,
    layoutParentPosition: { x: 0.5, y: 0.5 },
  };

  private readonly background = new Graphics().props({
    parent: this,
  });

  private readonly layoutContainer = new Container().props({
    parent: this,
  });

  constructor(initDisplaySettings: Partial<LayoutGalleryDisplaySettings> = {}) {
    super();

    Object.assign(this.displaySettings, initDisplaySettings);

    this.pixi.ticker.add(() => {
      this.pixi.stage.addChild(this);
    });

    registerResizableObject(this);
  }

  public show(): void {
    this.pixi.stage.addChild(this);
  }

  public addMenu(pane: Pane): void {
    const backgroundFolder = pane.addFolder({ title: 'Background ', expanded: false });
    [
      { title: 'black', color: 0x000000 },
      { title: 'white', color: 0xffffff },
    ].forEach(({ title, color }) => {
      backgroundFolder.addButton({ title }).on('click', () => {
        this.displaySettings.backgroundColor = color;
        this.redrawBackground();
      });
    });

    [0, 0.25, 0.5, 0.75, 1].forEach((alpha) => {
      backgroundFolder.addButton({ title: `Alpha ${alpha}` }).on('click', () => {
        this.displaySettings.backgroundAlpha = alpha;
        this.redrawBackground();
      });
    });

    const positionFolder = pane.addFolder({ title: 'Position', expanded: false });

    [
      { x: 0, y: 0 },
      { x: 0.5, y: 0 },
      { x: 1, y: 0 },
      { x: 0, y: 0.5 },
      { x: 0.5, y: 0.5 },
      { x: 1, y: 0.5 },
      { x: 0, y: 1 },
      { x: 0.5, y: 1 },
      { x: 1, y: 1 },
    ].forEach(({ x, y }) => {
      positionFolder.addButton({ title: `${x}x${y}` }).on('click', () => {
        this.displaySettings.layoutParentPosition.x = x;
        this.displaySettings.layoutParentPosition.y = y;
        this.positionLayoutParent();
      });
    });
  }

  public getDisplaySettings(): LayoutGalleryDisplaySettings {
    return this.displaySettings;
  }

  public setNewLayout(root: Container | null): void {
    this.layoutContainer.children.slice().forEach((child) => {
      child.destroy({ children: true });
    });
    if (root) this.layoutContainer.addChild(root);
  }

  private redrawBackground(): void {
    const { backgroundColor, backgroundAlpha } = this.displaySettings;
    const { width, height } = this.background.pixi.renderer;
    this.background.clear();
    this.background.rect(0, 0, width * 2, height * 2);
    this.background.fill(backgroundColor);
    this.background.alpha = backgroundAlpha;
  }

  private positionLayoutParent(): void {
    const { layoutParentPosition } = this.displaySettings;
    const { width, height } = this.layoutContainer.pixi.renderer;
    this.layoutContainer.props({
      x: layoutParentPosition.x * width,
      y: layoutParentPosition.y * height,
    });
  }

  public resize = (): void => {
    this.redrawBackground();
    this.positionLayoutParent();
  };
}
